import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Text,
  VStack,
  useColorMode,
} from "@chakra-ui/react";
import {
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiGrid,
  FiRotateCcw,
} from "react-icons/fi";
import { useMutation } from "@apollo/client";
import { useAuth } from "../contexts/AuthContext";
import { useModulePermissions } from "../contexts/ModulePermissionsContext";
import { useQuery } from "@apollo/client";
import { GET_MY_EMPLOYEE_PROFILE } from "../pages/employees/queries";
import { isOwnProfileIncomplete } from "../utils/employeeProfileCompleteness";
import { allModuleConfigs, moduleIcons } from "./FloatingNavbar";
import { SAVE_MY_QUICKLINKS } from "../pages/clients/queries";
import { ModuleConfig } from "../types/ModuleConfig";

// Some module configs store an emoji in `icon` (e.g. "👥"), others store a
// react-icons component name (e.g. "FiMessageSquare"). Render the actual icon
// for the latter — via the shared moduleIcons id→component map — instead of
// printing the raw name string.
const ModuleGlyph = ({ config }: { config: ModuleConfig }) => {
  const isIconName = /^Fi[A-Z]/.test(config.icon || "");
  if (isIconName) {
    const Comp = moduleIcons[config.id] || FiGrid;
    return <Icon as={Comp} boxSize={5} />;
  }
  return (
    <Text fontSize="xl" lineHeight="1">
      {config.icon}
    </Text>
  );
};

/**
 * AdminModuleLinks
 *
 * A quick-jump grid of the modules THIS USER is permitted to view, shown at the
 * TOP of the home page to ANY logged-in user. Each card opens its module in a
 * NEW TAB.
 *
 * Permission-filtered (2026-07-13): the grid keeps a module only if the user's
 * permissions intersect that module's backend-RESOLVED view/self/feedback — the
 * exact predicate FloatingNavbar.getAvailableModules uses — so the grid matches
 * the nav drawer. Most modules ship empty permission arrays in their moduleConfig
 * and get the real arrays from the backend SSOT (useModulePermissions) at runtime;
 * a module with no granted permission is hidden (no more "everything for any ADMIN").
 *
 * Curatable per-person (mobile-first), persisted to the CLIENT RECORD so it
 * follows the user across devices (phone ↔ laptop):
 *  - Edit mode turns each card into a tile with move ↑/↓ (reorder) and an eye
 *    toggle (hide/show). Big tap targets — no drag-and-drop, so it works on a
 *    phone where this is normally edited. Edit/Reset live at the BOTTOM.
 *  - Order + hidden set are seeded from `myClient.preferences.quickLinks` at
 *    login and saved via the `saveMyQuickLinks` mutation (debounced, optimistic).
 *  - Cross-device is eventual: another device sees changes on its next auth read.
 *
 * Self-gating: renders null unless authenticated AND ≥1 module is permitted.
 * Reuses the single canonical `allModuleConfigs` list from FloatingNavbar (no divergent copy).
 */

interface QuickLinkPrefs {
  order: string[]; // module ids, in the user's chosen order
  hidden: string[]; // module ids hidden from the (non-edit) grid
}

const EMPTY_PREFS: QuickLinkPrefs = { order: [], hidden: [] };

/**
 * Default GROUPED layout (Tom, 2026-07-16): with ~45 permitted modules a flat grid
 * is a wall of tiles, so until the user sets their own order the grid renders in
 * tiers. The raw config `category` values are messy (19 distinct, many singletons),
 * so they're folded into six stable groups here. A user's custom order switches to
 * the flat personalised grid; Reset returns to this grouped default.
 */
const CATEGORY_GROUPS: Array<{ label: string; categories: string[] }> = [
  { label: "Care & Clinical", categories: ["clinical", "healthcare", "quality"] },
  { label: "Rostering & People", categories: ["hr", "operations", "portal"] },
  { label: "Comms & Sales", categories: ["communication", "communications", "marketing", "crm", "sales"] },
  { label: "Business & Billing", categories: ["billing", "business", "core"] },
  { label: "Tools & Automation", categories: ["productivity", "tools", "automation", "intelligence", "education", "development", "security"] },
];
const OTHER_GROUP = "More";

const groupLabelFor = (config: ModuleConfig): string => {
  const cat = String((config as any).category || "").toLowerCase().trim();
  const hit = CATEGORY_GROUPS.find((g) => g.categories.includes(cat));
  return hit ? hit.label : OTHER_GROUP;
};

const GROUP_ORDER = [...CATEGORY_GROUPS.map((g) => g.label), OTHER_GROUP];

const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

// The first route that isn't a parameterised detail path (e.g. skip "/client/:id").
const resolveMainPath = (config: ModuleConfig): string | null => {
  const routePath = config.routes?.find((r) => r.path && !r.path.includes(":"))?.path;
  if (routePath) return routePath;
  const navPath = config.navigation?.find((n) => n.path && !n.path.includes(":"))?.path;
  return navPath || null;
};

/**
 * Where this tile should send THIS viewer.
 *
 * A module may declare `resolveLandingPath` and decide for itself (2026-08-21) — first-route
 * order is a poor proxy for intent: it sent managers to /employees/my-details and carers to
 * a view-gated /shifts. Modules that declare nothing keep the old behaviour exactly.
 */
const resolveLandingFor = (
  config: ModuleConfig,
  backendPerms: Record<string, any> | undefined,
  userPerms: string[] | undefined,
  ownProfileIncomplete: boolean,
): string | null => {
  if (config.resolveLandingPath) {
    const perms = (backendPerms as Record<string, any> | undefined)?.[config.id] ?? config.permissions;
    const hasLevel = (level: string): boolean => {
      const allowed = asStringArray((perms as Record<string, unknown> | undefined)?.[level]);
      return !!userPerms?.some((p) => allowed.includes(p));
    };
    const chosen = config.resolveLandingPath({ hasLevel, ownProfileIncomplete });
    if (chosen) return chosen;
  }
  return resolveMainPath(config);
};

interface ModuleItem {
  config: ModuleConfig;
  path: string;
}

const AdminModuleLinks = () => {
  const { isAuthenticated, user } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();
  const { colorMode } = useColorMode();

  const [editing, setEditing] = useState(false);
  const [prefs, setPrefs] = useState<QuickLinkPrefs>(EMPTY_PREFS);

  const [saveMutation] = useMutation(SAVE_MY_QUICKLINKS);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed from the Client record (myClient.preferences.quickLinks) whenever it
  // loads/changes. This is the source of truth — no localStorage.
  const serverPrefs = useMemo<QuickLinkPrefs>(() => {
    const ql = user?.preferences?.quickLinks;
    return { order: asStringArray(ql?.order), hidden: asStringArray(ql?.hidden) };
  }, [user?.preferences?.quickLinks]);

  useEffect(() => {
    setPrefs(serverPrefs);
  }, [serverPrefs]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Optimistic local update + debounced write to the DB (batches rapid taps).
  const savePrefs = useCallback(
    (next: QuickLinkPrefs) => {
      setPrefs(next);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        saveMutation({ variables: { order: next.order, hidden: next.hidden } }).catch(() => {
          /* transient network/auth error — local state already applied; retried on next edit */
        });
      }, 400);
    },
    [saveMutation]
  );

  // Enabled modules THIS USER may view, with a resolvable landing path, in their
  // default (config.order) order. The permission gate mirrors App.tsx
  // `resolvedModules` + FloatingNavbar `getAvailableModules`: merge the
  // backend-resolved permissions over each config (most ship empty arrays as the
  // SSOT placeholder), then keep a module only if the user's permissions
  // intersect its view/self/feedback. Empty resolved set ⇒ hidden — so the grid
  // shows exactly what the nav drawer does, never "everything for any ADMIN".
  const userPerms = user?.permissions;
  // Only self-service viewers need this: a manager's tile destination never depends on
  // their own profile, so managers don't pay for the query.
  const isEmployeesManager = !!userPerms?.some((p: string) =>
    asStringArray((backendPerms as Record<string, any> | undefined)?.employees?.view).includes(p),
  );
  const { data: ownProfileData } = useQuery(GET_MY_EMPLOYEE_PROFILE, {
    skip: !userPerms?.length || isEmployeesManager,
    fetchPolicy: "cache-first",
    errorPolicy: "ignore",
  });
  const ownProfileIncomplete = isOwnProfileIncomplete(ownProfileData?.myEmployeeProfile);
  const baseModules = useMemo<ModuleItem[]>(() => {
    if (!userPerms || userPerms.length === 0) return [];
    return allModuleConfigs
      .filter((m) => m.enabled !== false)
      .filter((m) => {
        const resolved = (backendPerms as Record<string, any> | undefined)?.[m.id];
        const perms = resolved ?? m.permissions;
        const gate: string[] = [
          ...((perms?.view as string[] | undefined) ?? []),
          ...((perms?.self as string[] | undefined) ?? []),
          ...((perms?.feedback as string[] | undefined) ?? []),
        ];
        return userPerms.some((p: string) => gate.includes(p));
      })
      .map((m) => ({
        config: m,
        path: resolveLandingFor(m, backendPerms as Record<string, any> | undefined, userPerms, ownProfileIncomplete),
      }))
      .filter((m): m is ModuleItem => !!m.path)
      .sort((a, b) => (a.config.order ?? 999) - (b.config.order ?? 999));
  }, [backendPerms, userPerms, ownProfileIncomplete]);

  // Apply the saved order; any module not yet in saved order (e.g. a newly
  // shipped module) falls in at the end in default order, so nothing vanishes.
  const orderedAll = useMemo<ModuleItem[]>(() => {
    const byId = new Map(baseModules.map((b) => [b.config.id, b]));
    const ordered: ModuleItem[] = [];
    prefs.order.forEach((id) => {
      const item = byId.get(id);
      if (item) {
        ordered.push(item);
        byId.delete(id);
      }
    });
    baseModules.forEach((b) => {
      if (byId.has(b.config.id)) ordered.push(b);
    });
    return ordered;
  }, [baseModules, prefs.order]);

  const hiddenSet = useMemo(() => new Set(prefs.hidden), [prefs.hidden]);

  const move = useCallback(
    (id: string, dir: -1 | 1) => {
      const ids = orderedAll.map((x) => x.config.id);
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      savePrefs({ ...prefs, order: ids });
    },
    [orderedAll, prefs, savePrefs]
  );

  const toggleHidden = useCallback(
    (id: string) => {
      const next = new Set(prefs.hidden);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      savePrefs({ ...prefs, hidden: Array.from(next) });
    },
    [prefs, savePrefs]
  );

  const reset = useCallback(() => savePrefs(EMPTY_PREFS), [savePrefs]);

  // --- gate AFTER all hooks (rules-of-hooks) ---
  // Shown to ANY logged-in user; baseModules is already filtered to the modules
  // this user is permitted to view. If they can view none, render nothing.
  // Employee-only profiles (nothing beyond USER + EMPLOYEE + LM_STUDENT) don't get the
  // admin quick-link grid — their world is My Details / My Shifts / the learning platform,
  // and this box just read as clutter on the home page (Tom, go-live weekend 2026-07-17).
  const isEmployeeOnly =
    (user?.permissions || []).length > 0 &&
    (user?.permissions || []).every((p: string) => p === "USER" || p === "EMPLOYEE" || p === "LM_STUDENT");

  if (!isAuthenticated || isEmployeeOnly || baseModules.length === 0) return null;

  const items = editing ? orderedAll : orderedAll.filter((x) => !hiddenSet.has(x.config.id));

  // Grouped default until the user sets a custom order (then their order wins flat).
  const isCustomOrder = prefs.order.length > 0;
  const groups: Array<{ label: string; items: ModuleItem[] }> = GROUP_ORDER.map((label) => ({
    label,
    items: items.filter((x) => groupLabelFor(x.config) === label),
  })).filter((g) => g.items.length > 0);

  const cardBg = colorMode === "light" ? "rgba(255,255,255,0.85)" : "rgba(30,30,30,0.7)";
  const cardBorder = colorMode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.12)";
  const textColor = colorMode === "light" ? "gray.800" : "gray.100";
  const labelColor = colorMode === "light" ? "gray.500" : "gray.400";

  return (
    <Container maxW="1200px" pt={6} pb={2} px={{ base: 4, md: 8 }} position="relative" zIndex={2}>
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="wide"
        textTransform="uppercase"
        color={labelColor}
        mb={editing ? 1 : 3}
      >
        Jump to a module{editing ? "" : " (opens in a new tab)"}
      </Text>

      {editing && (
        <Text fontSize="xs" color={labelColor} mb={3}>
          Reorder with ↑ ↓ (switches to your custom layout) · tap the eye to hide/show · Reset returns to the grouped default · saved to your account (syncs across devices)
        </Text>
      )}

      {items.length === 0 && !editing ? (
        <Text fontSize="sm" color={labelColor}>
          All modules hidden — tap <b>Edit</b> below to customise your quick links.
        </Text>
      ) : !editing && !isCustomOrder ? (
        /* Default GROUPED view — tiers by category until the user customises. */
        <VStack align="stretch" spacing={4}>
          {groups.map((group) => (
            <Box key={group.label}>
              <Text fontSize="xs" fontWeight="semibold" color={labelColor} mb={2} textTransform="uppercase" letterSpacing="wide">
                {group.label}
              </Text>
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={3}>
                {group.items.map(({ config, path }) => {
                  const accent = config.color || "blue.400";
                  return (
                    <Box
                      key={config.id}
                      as="a"
                      href={path}
                      target="_blank"
                      rel="noopener noreferrer"
                      textDecoration="none"
                      transition="all 0.2s"
                      _hover={{ transform: "translateY(-2px)", boxShadow: "md", borderColor: accent }}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={cardBorder}
                      borderLeft="3px solid"
                      borderLeftColor={accent}
                      borderRadius="xl"
                      p={3}
                      minW={0}
                    >
                      <VStack spacing={1} align="center">
                        <ModuleGlyph config={config} />
                        <Text color={textColor} fontWeight="semibold" fontSize="xs" textAlign="center" noOfLines={2} title={config.name}>
                          {config.name}
                        </Text>
                      </VStack>
                    </Box>
                  );
                })}
              </SimpleGrid>
            </Box>
          ))}
        </VStack>
      ) : (
        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={3}>
          {items.map(({ config, path }, index) => {
            const isHidden = hiddenSet.has(config.id);
            const accent = config.color || "blue.400";

            const inner = (
              <VStack spacing={1} align="center" opacity={editing && isHidden ? 0.4 : 1}>
                <ModuleGlyph config={config} />
                <Text
                  color={textColor}
                  fontWeight="semibold"
                  fontSize="xs"
                  textAlign="center"
                  noOfLines={2}
                  title={config.name}
                >
                  {config.name}
                </Text>
              </VStack>
            );

            // Shared card chrome
            const cardProps = {
              bg: cardBg,
              border: "1px solid",
              borderColor: cardBorder,
              borderLeft: "3px solid",
              borderLeftColor: accent,
              borderRadius: "xl",
              p: 3,
              minW: 0,
            } as const;

            if (!editing) {
              return (
                <Box
                  key={config.id}
                  as="a"
                  href={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  textDecoration="none"
                  transition="all 0.2s"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "md", borderColor: accent }}
                  {...cardProps}
                >
                  {inner}
                </Box>
              );
            }

            // Edit mode: tile with big tap targets, no navigation.
            return (
              <Box key={config.id} {...cardProps}>
                {inner}
                <HStack spacing={1} justify="center" mt={2}>
                  <IconButton
                    aria-label="Move earlier"
                    icon={<FiChevronUp />}
                    size="sm"
                    variant="ghost"
                    isDisabled={index === 0}
                    onClick={() => move(config.id, -1)}
                  />
                  <IconButton
                    aria-label="Move later"
                    icon={<FiChevronDown />}
                    size="sm"
                    variant="ghost"
                    isDisabled={index === items.length - 1}
                    onClick={() => move(config.id, 1)}
                  />
                  <IconButton
                    aria-label={isHidden ? "Show this module" : "Hide this module"}
                    icon={isHidden ? <FiEyeOff /> : <FiEye />}
                    size="sm"
                    variant="ghost"
                    colorScheme={isHidden ? "gray" : "blue"}
                    onClick={() => toggleHidden(config.id)}
                  />
                </HStack>
              </Box>
            );
          })}
        </SimpleGrid>
      )}

      {/* Controls at the BOTTOM (not top-right). */}
      <Flex justify="center" mt={4} gap={2}>
        {editing && (
          <Button size="sm" variant="ghost" colorScheme="red" leftIcon={<FiRotateCcw />} onClick={reset}>
            Reset
          </Button>
        )}
        <Button
          size="sm"
          variant={editing ? "solid" : "outline"}
          colorScheme={editing ? "green" : "gray"}
          leftIcon={editing ? <FiCheck /> : <FiEdit2 />}
          onClick={() => setEditing((e) => !e)}
        >
          {editing ? "Done" : "Edit"}
        </Button>
      </Flex>
    </Container>
  );
};

export default AdminModuleLinks;
